import urlConstructor from 'utils/urlConstructor';
import { put, call, takeLatest } from 'redux-saga/effects';
import API from 'types/api';

const paramsConstructorMap = {
  [API.POST_3RD_PARTY_AUTH]: urlConstructor.postAuth,
  [API.POST_3RD_PARTY_REGISTRATION]: urlConstructor.postRegistration,
  [API.POST_3RD_LOGOUT]: urlConstructor.postLogout,
};

const typesMap = {
  [API.POST_3RD_PARTY_AUTH]: API.POST_AUTH,
  [API.POST_3RD_PARTY_REGISTRATION]: API.POST_REGISTRATION,
  [API.POST_3RD_LOGOUT]: API.POST_LOGOUT,
};

export default function*() {
  yield takeLatest(API.POST_3RD_PARTY_AUTH, make3rdPartyRequest);
  yield takeLatest(API.POST_3RD_PARTY_REGISTRATION, make3rdPartyRequest);
  yield takeLatest(API.POST_3RD_LOGOUT, make3rdPartyRequest);
}

function* make3rdPartyRequest({ type, payload }) {
  const timeout = 5000;
  const debug = self.debug('make3rdPartyRequest');
  const side = 580;
  const { url, scriptUrl } = getAuthResources();
  const features = `
width=${side}, 
height=${side}, 
top=${(window.innerHeight - side) / 2},
left=${(window.innerWidth - side) / 2}
`;
  const authWindow = window.open(url, '_blank', features);

  if (!authWindow) {
    throw new Error('Creating authentication window failure.');
  }

  authWindow.addEventListener('load', onAuthWindowReady, false);
  window.focus();

  const afterAll = [
    () => authWindow.removeEventListener('load', onAuthWindowReady, false),
    () => authWindow && authWindow.close(),
  ];

  yield put({ type: `${typesMap[type]}_FETCH` });

  try {
    const response = yield call(authenticate, { createTimeout });
    const nextAction = { type: `${typesMap[type]}_SUCCESS`, payload: response.data };
    debug('next action: %O', nextAction);
    yield put(nextAction);
  } catch (err) {
    yield put({ type: `${typesMap[type]}_ERROR`, payload: err || {} });
  }

  afterAll.forEach((fnc) => fnc());

  function authenticate({ createTimeout }) {
    return Promise.race([
      new Promise(createTimeout()),
      new Promise((resolve, reject) => {
        function onMessage(event) {
          debug('parent window got message: %O', event);
          /** @type module:MggEmbed/Auth.WindowMessage */
          const message = event.data;
          if (-1 === document.location.origin.indexOf(event.origin)) {
            debug('omit handle message from other origin');
            return;
          }
          if (!('userResponse' === message.name)) {
            debug('omit handle unexpected message');
            return;
          }
          debug('handle message');

          const response = JSON.parse(message.value.response);

          if (response.result === 'error') {
            debug('Authentication rejected %O', response);
            reject({ code: response.code, response });
          } else {
            debug('authentication success, response: %O', response);
            resolve(response);
          }
        }

        window.addEventListener('message', onMessage);
        afterAll.push(() => window.removeEventListener('message', onMessage));
      }),
      new Promise((resolve) => {
        function watchLocalStorageUpdates(event) {
          if (event.key !== 'userResponse') {
            return;
          }
          const data = JSON.parse(event.newValue);

          resolve(data);
        }

        self.addEventListener('storage', watchLocalStorageUpdates);
        afterAll.push(() => self.removeEventListener('storage', watchLocalStorageUpdates));
      }),
    ]);
  }

  function createTimeout() {
    const fail = {
      code: 200,
      result: 'error',
      response: {
        errors: [
          {
            message: 'Timeout on window communication',
            field: 'login',
          },
        ],
      },
    };
    return (resolve, reject) => window.setTimeout(reject, timeout, fail);
  }

  function onAuthWindowReady(event) {
    debug('Authentication window ready. %O', event);
    authWindow.postMessage(getScriptInstruction(), url);
  }

  function getScriptInstruction() {
    const [reqUrl, params] = paramsConstructorMap[type](payload);
    const args = {
      params,
      reqUrl,
    };

    return { name: 'scriptUrl', value: scriptUrl, args };
  }
}

function getAuthResources() {
  const authResources = {
    scriptUrl: 'https://s1.vcdn.biz/static/71196741/childRequestScript.js',
    url: `${window.location.origin}/auth_popup.html`,
  };

  if (process.env.NODE_ENV !== 'production') {
    authResources.url = `${window.location.origin}/thirdPartyRequest/auth_popup.html`;
    authResources.scriptUrl = `${window.location.origin}/thirdPartyRequest/childRequestScript.js`;
  }

  return authResources;
}
